// "Owning a home here" detail block — shared between CountryScoreCard
// (atlas catalogue) and DetailsPanel (comune view, IT only).
//
// Two layouts:
//   - full (default): vertical rows with bold field labels + supporting
//     text. Used inside a <details> accordion or a section card.
//   - compact: tighter rows with smaller type for embedding inside a
//     side panel (DetailsPanel) where vertical space is precious.

import { useTranslation } from 'react-i18next';
import {
  formatTierLabel,
  getCountryTax,
  isHighConfidence,
  usePropertyTax,
  type AnnualPropertyTax,
  type CapitalGainsTax,
  type PropertyTaxTier,
  type PurchaseTax,
  type WealthTaxRealEstate,
} from './data';
import { uiLang } from '@/i18n/lang';

export interface PropertyTaxDetailProps {
  countryCode: string;
  /** When true, renders tighter type / smaller spacing for side-panel use. */
  compact?: boolean;
}

type TFn = (key: string, opts?: Record<string, unknown>) => string;

export function PropertyTaxDetail({ countryCode, compact = false }: PropertyTaxDetailProps) {
  const { t, i18n } = useTranslation();
  const lang: 'en' | 'ru' = uiLang(i18n.language);
  const { data: file, isLoading } = usePropertyTax();
  if (isLoading) {
    return (
      <p
        className="font-mono text-[11px] text-ink-400"
        data-testid={`property-tax-loading-${countryCode}`}
      >
        {t('propertyTax.loading', { defaultValue: 'Loading…' })}
      </p>
    );
  }
  const entry = getCountryTax(file, countryCode);
  if (!entry) {
    return (
      <p
        className="font-mono text-[11px] text-ink-400"
        data-testid={`property-tax-empty-${countryCode}`}
      >
        {t('propertyTax.noData', {
          defaultValue: 'No property-tax data for this country yet.',
        })}
      </p>
    );
  }

  if (!isHighConfidence(entry)) {
    return (
      <div
        className="rounded-md border border-ink-100 bg-paper-2 px-3 py-2 text-xs leading-snug text-ink-700"
        data-testid={`property-tax-placeholder-${countryCode}`}
      >
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-500">
          {t('propertyTax.label', { defaultValue: 'Property tax' })} ·{' '}
          {tierWord(t, entry.tier, lang)}
        </span>
        <p className="mt-1">
          {t('propertyTax.placeholder', {
            defaultValue:
              "Specific rates for this country are still being verified — ask the AI helper or a consultant for current numbers.",
          })}
        </p>
      </div>
    );
  }

  const ap = entry.annual_property_tax;
  const pt = entry.purchase_tax;
  const cg = entry.capital_gains_tax;
  const wt = entry.wealth_tax_real_estate;
  const fr = entry.foreigner_regime;
  const rowCls = compact
    ? 'flex flex-col gap-0.5 border-b border-ink-100 py-1.5 last:border-0'
    : 'flex flex-col gap-1 border-b border-ink-100 py-2 last:border-0';
  const labelCls = compact
    ? 'font-mono text-[10px] uppercase tracking-wider text-ink-500'
    : 'font-mono text-[11px] uppercase tracking-wider text-ink-500';
  const valCls = compact
    ? 'text-[12px] leading-snug text-ink-800'
    : 'text-sm leading-snug text-ink-800';
  const noteCls = compact ? 'text-[11px] text-ink-500' : 'text-[12px] text-ink-500';

  return (
    <div
      className="rounded-md border border-ink-100 bg-paper-2 px-3 py-2"
      data-testid={`property-tax-detail-${countryCode}`}
    >
      <header className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-500">
          {t('propertyTax.label', { defaultValue: 'Property tax' })}
        </span>
        <TierBadge tier={entry.tier} t={t} lang={lang} />
      </header>

      {ap && (
        <div className={rowCls}>
          <span className={labelCls}>
            {t('propertyTax.row.annual', { defaultValue: 'Annual tax' })}
            {ap.name_local ? ` · ${ap.name_local}` : ''}
          </span>
          <span className={valCls}>{formatAnnual(ap, t, lang) ?? '—'}</span>
          {(ap.notes_en || ap.notes_ru) && (
            <span className={noteCls}>
              {lang === 'ru' ? ap.notes_ru ?? ap.notes_en : ap.notes_en}
            </span>
          )}
        </div>
      )}

      {pt && (
        <div className={rowCls}>
          <span className={labelCls}>
            {t('propertyTax.row.purchase', { defaultValue: 'Purchase tax (one-shot)' })}
          </span>
          <span className={valCls}>{formatPurchase(pt, t) ?? '—'}</span>
          {pickNote(pt.notes_en, pt.notes_ru, lang) && (
            <span className={noteCls}>
              {pickNote(pt.notes_en, pt.notes_ru, lang)}
            </span>
          )}
        </div>
      )}

      {cg && (
        <div className={rowCls}>
          <span className={labelCls}>
            {t('propertyTax.row.capitalGains', { defaultValue: 'Capital gains on sale' })}
          </span>
          <span className={valCls}>{formatCapitalGains(cg, t) ?? '—'}</span>
          {pickNote(cg.notes_en, cg.notes_ru, lang) && (
            <span className={noteCls}>
              {pickNote(cg.notes_en, cg.notes_ru, lang)}
            </span>
          )}
        </div>
      )}

      {wt?.applies && (
        <div className={rowCls}>
          <span className={labelCls}>
            {t('propertyTax.row.wealth', { defaultValue: 'Wealth tax on real estate' })}
            {wt.name_local ? ` · ${wt.name_local}` : ''}
          </span>
          <span className={valCls}>{formatWealth(wt, t) ?? '—'}</span>
          {pickNote(wt.notes_en, wt.notes_ru, lang) && (
            <span className={noteCls}>
              {pickNote(wt.notes_en, wt.notes_ru, lang)}
            </span>
          )}
        </div>
      )}

      {fr?.name_en && (
        <div className={rowCls}>
          <span className={labelCls}>
            {t('propertyTax.row.regime', { defaultValue: 'Foreigner regime' })}
          </span>
          <span className={valCls}>
            {lang === 'ru' ? fr.name_ru ?? fr.name_en : fr.name_en}
          </span>
          {pickNote(fr.notes_en, fr.notes_ru, lang) && (
            <span className={noteCls}>
              {pickNote(fr.notes_en, fr.notes_ru, lang)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function pickNote(en?: string | null, ru?: string | null, lang?: 'en' | 'ru'): string | null {
  if (lang === 'ru') return ru ?? en ?? null;
  return en ?? null;
}

function tierWord(t: TFn, tier: PropertyTaxTier, lang: 'en' | 'ru'): string {
  return t(`propertyTax.tier.${tier}`, { defaultValue: formatTierLabel(tier, lang) });
}

function TierBadge({ tier, t, lang }: { tier: PropertyTaxTier; t: TFn; lang: 'en' | 'ru' }) {
  const palette: Record<PropertyTaxTier, string> = {
    low: 'border-moss-200 bg-moss-100 text-moss-700',
    medium: 'border-ochre-200 bg-ochre-100 text-ochre-700',
    high: 'border-rust-200 bg-rust-100 text-rust-700',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[11px] capitalize ${palette[tier]}`}
    >
      {tierWord(t, tier, lang)}
    </span>
  );
}

function rangePct(min?: number | null, max?: number | null): string | null {
  if (min == null && max == null) return null;
  if (min == null) return `${max}%`;
  if (max == null) return `${min}%`;
  if (min === max) return `${min}%`;
  return `${min}–${max}%`;
}

function formatAnnual(ap: AnnualPropertyTax, t: TFn, lang: 'en' | 'ru'): string | null {
  const range = rangePct(ap.second_home_pct_min, ap.second_home_pct_max);
  const primary = ap.primary_residence_pct;
  // Sentinel: name says "None" / "Abolished*" OR all rates are 0 → no annual tax.
  // Guard BEFORE the numeric branches: rangePct(0,0) returns truthy '0%', which
  // would otherwise emit nonsense like "0% (second home) · primary exempt".
  const noAnnualTax =
    ap.name_en === 'None' ||
    /^Abolished/i.test(ap.name_en ?? '') ||
    ((ap.second_home_pct_min ?? null) === 0 &&
      (ap.second_home_pct_max ?? null) === 0 &&
      (primary ?? 0) === 0);
  if (noAnnualTax) {
    // `name_en` drives the sentinel above, but the visible string follows the
    // UI language — a Russian card used to read "None" / "Abolished 2017".
    const name = lang === 'ru' ? ap.name_ru ?? ap.name_en : ap.name_en;
    return name || t('propertyTax.value.noAnnualTax', { defaultValue: 'No annual tax' });
  }
  if (!range && primary == null) return null;
  const second = t('propertyTax.value.secondHome', { defaultValue: 'second home' });
  const primExempt = t('propertyTax.value.primaryExempt', { defaultValue: 'primary exempt' });
  const primaryLabel = t('propertyTax.value.primary', { defaultValue: 'primary' });
  if (primary === 0 && range) return `${range} (${second}) · ${primExempt}`;
  if (primary != null && range) return `${primary}% ${primaryLabel} / ${range} ${second}`;
  return range;
}

function formatPurchase(pt: PurchaseTax, t: TFn): string | null {
  const primary = rangePct(pt.primary_residence_pct_min, pt.primary_residence_pct_max);
  const second = rangePct(pt.second_home_pct_min, pt.second_home_pct_max);
  const primaryLabel = t('propertyTax.value.primary', { defaultValue: 'primary' });
  const secondLabel = t('propertyTax.value.secondHome', { defaultValue: 'second home' });
  if (primary === second) return primary ?? null;
  if (primary && second) return `${primary} ${primaryLabel} / ${second} ${secondLabel}`;
  return primary ?? second ?? null;
}

function formatCapitalGains(cg: CapitalGainsTax, t: TFn): string | null {
  const parts: string[] = [];
  if (cg.rate_pct != null) {
    parts.push(`${cg.rate_pct}%`);
  } else if (cg.rate_pct_min != null && cg.rate_pct_max != null) {
    parts.push(`${cg.rate_pct_min}–${cg.rate_pct_max}%`);
  }
  if (cg.primary_residence_exempt || cg.primary_residence_rollover_exempt) {
    parts.push(t('propertyTax.value.primaryExempt', { defaultValue: 'primary exempt' }));
  } else if (cg.primary_residence_exempt_after_years === 0) {
    // Sentinel: 0 years = immediately exempt (always exempt). Italy uses this.
    parts.push(t('propertyTax.value.primaryAlwaysExempt', { defaultValue: 'primary always exempt' }));
  } else if (cg.primary_residence_exempt_after_years != null) {
    parts.push(
      t('propertyTax.value.primaryExemptAfter', {
        defaultValue: 'primary exempt after {{years}}y',
        years: cg.primary_residence_exempt_after_years,
      }),
    );
  } else if (cg.primary_residence_exempt_for_seniors) {
    parts.push(t('propertyTax.value.over65PrimaryExempt', { defaultValue: 'over-65 primary exempt' }));
  } else if (cg.primary_residence_lifetime_exemption_eur != null) {
    parts.push(
      t('propertyTax.value.lifetimeExemption', {
        defaultValue: '€{{amount}} lifetime exemption',
        amount: cg.primary_residence_lifetime_exemption_eur.toLocaleString('en-US'),
      }),
    );
  }
  if (cg.second_home_holding_years_exempt != null) {
    parts.push(
      t('propertyTax.value.secondHomeExemptAfter', {
        defaultValue: 'second home exempt after {{years}}y',
        years: cg.second_home_holding_years_exempt,
      }),
    );
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

function formatWealth(wt: WealthTaxRealEstate, t: TFn): string | null {
  const parts: string[] = [];
  const range = rangePct(wt.rate_pct_min, wt.rate_pct_max);
  if (range) parts.push(range);
  if (wt.threshold_eur != null) {
    parts.push(
      t('propertyTax.value.threshold', {
        defaultValue: '€{{amount}}k threshold',
        amount: (wt.threshold_eur / 1000).toFixed(0),
      }),
    );
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}
